import {
  getDeleteErpCustomerUserErrorMessage,
  getErpCustomerUsers,
  getErpCustomerUsersLoading,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersSelectors';
import { resetDeleteErpCustomerUserErrorMessage } from 'gcs-common/slices/administration/erpCustomers/erpCustomersSlice';
import {
  deleteErpCustomerUser,
  fetchErpCustomerUsers,
} from 'gcs-common/slices/administration/erpCustomers/erpCustomersThunks';
import { getHasPermission } from 'gcs-common/slices/currentUser/currentUserSelectors';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { stringSortingFunction } from '../../../../helper/sortHelper';
import deletedIcon from '../../../../img/delete.svg';
import editIcon from '../../../../img/edit.svg';
import addIcon from '../../../../img/plus_white.svg';
import {
  showFailureNotification,
  showSuccessNotification,
} from '../../../../slices/notifications/ShowNotificationThunk';
import AddFabButton from '../../../AddFabButton/AddFabButton';
import PopUpModal from '../../../PopUpModal/PopUpModal';
import { Spinner } from '../../../Spinner/Spinner';
import ProfileImageCell from '../../../Table/ProfileImageCell/ProfileImageCell';
import Table from '../../../Table/Table';
import ErpCustomerUserCreate from '../ErpCustomerUserCreate/ErpCustomerUserCreate';
import ErpCustomerUserUpdate from '../ErpCustomerUserUpdate/ErpCustomerUserUpdate';
import ErpCustomerUserDelete from './ErpCustomerUserDelete/ErpCustomerUserDelete';
import styles from './styles.module.scss';

const userColumns = [
  {
    Header: 'Profilbild',
    accessor: 'imageUrl',
    Cell: ProfileImageCell,
    disableSortBy: true,
  },
  {
    Header: 'Name',
    accessor: 'friendlyName',
    filter: 'fuzzyText',
    sortType: stringSortingFunction,
  },
  {
    Header: 'Angegebener Firmenname',
    accessor: 'customerCompany',
    filter: 'fuzzyText',
    sortType: stringSortingFunction,
  },
  {
    Header: 'Telefonnummer',
    accessor: 'phoneNumber',
    filter: 'fuzzyText',
  },
  {
    Header: 'Email',
    accessor: 'email',
    filter: 'fuzzyText',
  },
  {
    Header: 'Datenschutz akzeptiert',
    accessor: 'privacyAccepted',
  },
];

const ErpCustomerUserTable = ({ erpCustomerId, searchInputValue = '' }) => {
  const erpCustomerUsers = useSelector(getErpCustomerUsers);
  const erpCustomerUsersLoading = useSelector(getErpCustomerUsersLoading);
  const dispatch = useDispatch();
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [showUserCreate, setShowUserCreate] = useState(false);
  const [showUserUpdate, setShowUserUpdate] = useState(false);
  const [erpCustomerUserToUpdate, setErpCustomerUserToUpdate] = useState(null);
  const defaultErrorMessage = useSelector(getDeleteErpCustomerUserErrorMessage);
  const [erpCustomerUserToDelete, setErpCustomerUserToDelete] = useState(null);
  const [userIdsToDelete, setUserIdsToDelete] = useState([]);
  const isGlobalAdmin = useSelector(getHasPermission('global_admin'));

  useEffect(() => {
    if (erpCustomerId) {
      dispatch(fetchErpCustomerUsers({ erpCustomerId }));
    }
  }, [erpCustomerId, dispatch]);

  const erpCustomerUserCreated = useCallback(() => {
    setShowUserCreate(false);
    dispatch(showSuccessNotification('Erfolgreich hinzugefügt'));
  }, [dispatch]);

  const erpCustomerUserUpdated = () => {
    setShowUserUpdate(false);
    dispatch(showSuccessNotification('Erfolgreich hinzugefügt'));
    dispatch(fetchErpCustomerUsers({ erpCustomerId }));
  };

  const closeDeleteModal = useCallback(() => {
    setErpCustomerUserToDelete(null);
    setShowDeleteModal(false);
  }, []);

  const removeFromUserIdsToDelete = useCallback(
    (userId) => {
      const filteredUserIds = userIdsToDelete.filter((id) => id !== userId);
      setUserIdsToDelete(filteredUserIds);
    },
    [userIdsToDelete],
  );

  const addToUserIdsToDelete = useCallback(
    (userId) => {
      const userIds = [...userIdsToDelete, userId];
      setUserIdsToDelete(userIds);
    },
    [userIdsToDelete],
  );

  const handleDelete = useCallback(async () => {
    const userId = erpCustomerUserToDelete?.id;
    addToUserIdsToDelete(userId);
    setShowDeleteModal(null);
    const result = await dispatch(
      deleteErpCustomerUser({
        userId,
        erpCustomerId,
      }),
    );
    removeFromUserIdsToDelete(userId);
    setErpCustomerUserToDelete(null);
    if (!result.error) {
      dispatch(showSuccessNotification('Erfolgreich entfernt'));
    }
  }, [erpCustomerUserToDelete, addToUserIdsToDelete, dispatch, erpCustomerId, removeFromUserIdsToDelete]);

  const deleteSelected = useCallback((event, rowData) => {
    event.stopPropagation();
    const { original } = rowData;
    setErpCustomerUserToDelete(original);
    setShowDeleteModal(true);
  }, []);

  const editSelected = (event, rowData) => {
    event.stopPropagation();
    const { original } = rowData;
    setErpCustomerUserToUpdate(original);
    setShowUserUpdate(true);
  };

  useEffect(() => {
    if (defaultErrorMessage) {
      dispatch(showFailureNotification(defaultErrorMessage, dispatch(resetDeleteErpCustomerUserErrorMessage())));
    }
  }, [defaultErrorMessage, dispatch]);

  const extendedErpCustomerUsers = useMemo(() => {
    return erpCustomerUsers.map((user) => {
      if (userIdsToDelete.includes(user.id)) {
        return { ...user, isDeleting: true };
      }
      return user;
    });
  }, [erpCustomerUsers, userIdsToDelete]);

  return (
    <>
      <Table
        isLoading={erpCustomerUsersLoading}
        data={extendedErpCustomerUsers}
        columns={userColumns}
        filterString={searchInputValue}
        renderActions={({ row }) => (
          <>
            {row.original.isDeleting ? (
              <Spinner />
            ) : (
              <div className={styles.actionButtons}>
                {isGlobalAdmin && (
                  <button type="button">
                    <img alt="edit-icon" src={editIcon} onClick={(event) => editSelected(event, row)} />
                  </button>
                )}
                <button type="button">
                  <img alt="deleted-icon" src={deletedIcon} onClick={(event) => deleteSelected(event, row)} />
                </button>
              </div>
            )}
          </>
        )}
      />
      <AddFabButton title="Nutzer" icon={addIcon} onClick={() => setShowUserCreate(true)} />
      {showUserCreate && (
        <PopUpModal title="Neuer nutzer" onRequestClose={() => setShowUserCreate(false)} isOpen>
          <ErpCustomerUserCreate
            erpCustomerId={erpCustomerId}
            onCancel={() => setShowUserCreate(false)}
            onSuccess={erpCustomerUserCreated}
          />
        </PopUpModal>
      )}
      {isGlobalAdmin && showUserUpdate && erpCustomerUserToUpdate && (
        <PopUpModal title="Nutzerdaten bearbeiten" onRequestClose={() => setShowUserUpdate(false)} isOpen>
          <ErpCustomerUserUpdate
            user={erpCustomerUserToUpdate}
            erpCustomerId={erpCustomerId}
            onCancel={() => setShowUserUpdate(false)}
            onSuccess={erpCustomerUserUpdated}
          />
        </PopUpModal>
      )}
      {showDeleteModal && (
        <PopUpModal title="NUTZER löschen" onRequestClose={closeDeleteModal} isOpen>
          <ErpCustomerUserDelete
            erpCustomerUser={erpCustomerUserToDelete}
            onCancel={closeDeleteModal}
            handleDelete={handleDelete}
          />
        </PopUpModal>
      )}
    </>
  );
};

ErpCustomerUserTable.propTypes = {
  searchInputValue: PropTypes.string,
  erpCustomerId: PropTypes.string.isRequired,
};

export default ErpCustomerUserTable;
